import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  communityPlatformIcon,
  communityPlatformLabel,
  type CommunityChannel,
  type NetworkResource,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { signStaffResourceUrl } from '@/lib/bunnyStorage';
import { useToast } from '@/features/ui/toast/useToast';
import './network.css';

async function openResource(doc: NetworkResource) {
  if (doc.external_url) {
    window.open(doc.external_url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (doc.storage_path) {
    const url = await signStaffResourceUrl({ storagePath: doc.storage_path });
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  throw new Error('Aucun fichier associé');
}

export function NetworkGuidesPage() {
  const toast = useToast();
  const [rows, setRows] = useState<NetworkResource[]>([]);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from('network_resources')
      .select(
        'id, kind, title, description, file_name, storage_path, external_url, video_url, duration_label, is_published, sort_order',
      )
      .eq('kind', 'guide')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (err) toast.error(err.message);
        else setRows((data ?? []) as NetworkResource[]);
      });
  }, [toast]);

  return (
    <div className="u-page nk-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Documents PDF, Excel ou PowerPoint à télécharger.</p>
        </div>
        <Link className="btn btn-ghost" to="/app/reseau">
          Retour
        </Link>
      </div>
      <section className="nk-panel">
        {rows.length === 0 ? (
          <p className="u-empty">Aucun guide publié pour le moment.</p>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="nk-resource-row">
              <div>
                <strong>{r.title}</strong>
                {r.description ? <p className="muted" style={{ margin: '0.3rem 0 0' }}>{r.description}</p> : null}
                {r.file_name ? (
                  <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                    {r.file_name}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={opening === r.id}
                onClick={() => {
                  setOpening(r.id);
                  void openResource(r)
                    .catch((e) => toast.error(e instanceof Error ? e.message : 'Téléchargement impossible'))
                    .finally(() => setOpening(null));
                }}
              >
                {opening === r.id ? 'Ouverture…' : 'Télécharger'}
              </button>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

export function NetworkMasterclassPage() {
  const toast = useToast();
  const [rows, setRows] = useState<NetworkResource[]>([]);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    void supabase
      .from('network_resources')
      .select(
        'id, kind, title, description, file_name, storage_path, external_url, video_url, duration_label, is_published, sort_order',
      )
      .eq('kind', 'masterclass')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false })
      .then(({ data, error: err }) => {
        if (err) toast.error(err.message);
        else setRows((data ?? []) as NetworkResource[]);
      });
  }, [toast]);

  async function openVideo(doc: NetworkResource) {
    if (doc.video_url) {
      window.open(doc.video_url, '_blank', 'noopener,noreferrer');
      return;
    }
    await openResource(doc);
  }

  return (
    <div className="u-page nk-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Vidéos des mentors et consultants de la communauté ISBD.</p>
        </div>
        <Link className="btn btn-ghost" to="/app/reseau">
          Retour
        </Link>
      </div>
      <section className="nk-panel">
        {rows.length === 0 ? (
          <p className="u-empty">Aucune masterclass publiée pour le moment.</p>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="nk-resource-row">
              <div>
                <strong>{r.title}</strong>
                {r.description ? <p className="muted" style={{ margin: '0.3rem 0 0' }}>{r.description}</p> : null}
                {r.duration_label ? (
                  <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                    Durée : {r.duration_label}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={opening === r.id}
                onClick={() => {
                  setOpening(r.id);
                  void openVideo(r)
                    .catch((e) => toast.error(e instanceof Error ? e.message : 'Lecture impossible'))
                    .finally(() => setOpening(null));
                }}
              >
                {opening === r.id ? 'Ouverture…' : 'Regarder'}
              </button>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

type CommunityView = CommunityChannel & { resolvedImage?: string };

export function NetworkCommunitiesPage() {
  const toast = useToast();
  const [rows, setRows] = useState<CommunityView[]>([]);

  useEffect(() => {
    void (async () => {
      const { data, error: err } = await supabase
        .from('community_channels')
        .select(
          'id, platform, label, url, description, sort_order, is_active, image_url, image_storage_path, created_at',
        )
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('label', { ascending: true });
      if (err) {
        toast.error(err.message);
        return;
      }
      const list = (data ?? []) as CommunityView[];
      const withImages = await Promise.all(
        list.map(async (c) => {
          if (c.image_url || !c.image_storage_path) return c;
          try {
            const url = await signStaffResourceUrl({ storagePath: c.image_storage_path });
            return { ...c, resolvedImage: url };
          } catch {
            return c;
          }
        }),
      );
      setRows(withImages);
    })();
  }, [toast]);

  return (
    <div className="u-page nk-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">
            Rejoignez les espaces ISBD : WhatsApp, Facebook, TikTok, Telegram, LinkedIn et plus encore.
          </p>
        </div>
        <Link className="btn btn-ghost" to="/app/reseau">
          Retour
        </Link>
      </div>
      {rows.length === 0 ? (
        <p className="u-empty">
          Aucune communauté active pour le moment. L’équipe ISBD publiera bientôt les liens d’invitation.
        </p>
      ) : (
        <div className="nk-community-grid">
          {rows.map((c) => (
            <article key={c.id} className="nk-community-card">
              <div className="nk-community-card-img">
                {c.resolvedImage || c.image_url ? (
                  <img src={c.resolvedImage || c.image_url || ''} alt="" />
                ) : (
                  <span aria-hidden style={{ fontSize: '2.25rem' }}>
                    {communityPlatformIcon(c.platform)}
                  </span>
                )}
              </div>
              <div className="nk-community-card-body">
                <span className="nk-tag" style={{ width: 'fit-content' }}>
                  {communityPlatformLabel(c.platform)}
                </span>
                <strong>{c.label}</strong>
                <p>{c.description || `Rejoindre la communauté ${communityPlatformLabel(c.platform)}`}</p>
                <a className="btn btn-primary" href={c.url} target="_blank" rel="noopener noreferrer">
                  Rejoindre
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
